/**
 * Datos de configuración para cada tipo de campo
 *
 * Este archivo contiene la estructura de datos que define qué campos
 * mostrar en el modal de configuración para cada tipo de campo de entidad.
 */

import type { FieldConfigurations } from "./fieldConfigurations";

export const fieldConfigurationsData: FieldConfigurations = [
  {
    field: "phone",
    sections: [
      {
        title: "Detalles",
        fields: [
          {
            key: "fieldName",
            type: "text",
            label: "Nombre del campo",
            placeholder: "Nombre del campo",
            required: true,
          },
        ],
      },
      {
        title: "Opciones",
        showSeparator: true,
        fields: [
          {
            key: "hasDropdownButton",
            type: "switch",
            label: "¿Agregar botón dropdown al campo?",
            required: true,
            defaultValue: false,
          },
          {
            key: "buttonPosition",
            type: "select",
            label: "Posición del botón",
            required: true,
            options: [
              { value: "left", label: "Izquierda" },
              { value: "right", label: "Derecha" },
            ],
            defaultValue: "left",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueSource",
            type: "radio",
            label: "¿De dónde provienen los valores?",
            required: true,
            options: [
              { value: "database", label: "Desde centro de datos" },
              { value: "manual", label: "Locales" },
            ],
            defaultValue: "database",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueDatacenter",
            type: "datacenter",
            required: true,
            label: "Centro de datos",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
          {
            key: "localList",
            type: "local_list",
            label: "Lista local",
            required: true,
            helperText:
              "El primer valor de la lista será el pre-seleccionado por default",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "manual",
          },
          {
            key: "multipleSelection",
            type: "switch",
            label: "Listado de selección múltiple",
            required: false,
            defaultValue: false,
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "manual",
          },
          {
            key: "expandSelectedRecord",
            type: "switch",
            label: "Expandir registro seleccionado",
            required: true,
            defaultValue: false,
            customProps: {
              switchFalseText: "No, generar una sola variable (array)",
            },
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
        ],
      },
      {
        title: "Configuración",
        showSeparator: true,
        fields: [
          {
            key: "placeholder",
            type: "text",
            label: "Placeholder del campo",
            placeholder: "Placeholder del campo",
            required: true,
          },
          {
            key: "defaultValue",
            type: "default_value",
            label: "Valor por defecto",
            placeholder: "Escribe un valor por defecto",
            helperText:
              "Si está vacío no se aplicará ningún valor por defecto.",
            defaultValue: "",
            customProps: {
              prefixText: "inp",
              helperTextIcon: "circle-info",
              helperTextColor: "text-info",
            },
          },
        ],
      },
    ],
  },
  {
    field: "identification",
    sections: [
      {
        title: "Detalles",
        fields: [
          {
            key: "fieldName",
            type: "text",
            label: "Nombre del campo",
            placeholder: "Nombre del campo",
            required: true,
          },
        ],
      },
      {
        title: "Opciones",
        showSeparator: true,
        fields: [
          {
            key: "hasDropdownButton",
            type: "switch",
            label: "¿Agregar botón dropdown al campo?",
            required: true,
            defaultValue: false,
          },
          {
            key: "buttonPosition",
            type: "select",
            label: "Posición del botón",
            required: true,
            options: [
              { value: "left", label: "Izquierda" },
              { value: "right", label: "Derecha" },
            ],
            defaultValue: "left",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueSource",
            type: "radio",
            label: "¿De dónde provienen los valores?",
            required: true,
            options: [
              { value: "database", label: "Desde centro de datos" },
              { value: "manual", label: "Locales" },
            ],
            defaultValue: "database",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueDatacenter",
            type: "datacenter",
            required: true,
            label: "Centro de datos",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
          {
            key: "localList",
            type: "local_list",
            label: "Lista local",
            required: true,
            helperText:
              "El primer valor de la lista será el pre-seleccionado por default",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "manual",
          },
          {
            key: "multipleSelection",
            type: "switch",
            label: "Listado de selección múltiple",
            required: false,
            defaultValue: false,
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "manual",
          },
          {
            key: "expandSelectedRecord",
            type: "switch",
            label: "Expandir registro seleccionado",
            required: true,
            defaultValue: false,
            customProps: {
              switchFalseText: "No, generar una sola variable (array)",
            },
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
        ],
      },
      {
        title: "Configuración",
        showSeparator: true,
        fields: [
          {
            key: "allowUserChooseDocType",
            type: "switch",
            label: "¿Dejar al usuario elegir el tipo de documento?",
            required: true,
            defaultValue: false,
          },
          {
            key: "documentType",
            type: "select",
            label: "Tipo de documento",
            required: true,
            options: [
              { value: "dui", label: "DUI" },
              { value: "nit", label: "NIT" },
              { value: "passport", label: "Pasaporte" },
            ],
            defaultValue: "dui",
            condition: (formValues) =>
              formValues.allowUserChooseDocType === false,
          },
          {
            key: "defaultValue",
            type: "default_value",
            label: "Valor por defecto",
            placeholder: "Escribe un valor por defecto",
            helperText:
              "Si está vacío no se aplicará ningún valor por defecto.",
            defaultValue: "",
            customProps: {
              prefixText: "inp",
              helperTextIcon: "circle-info",
              helperTextColor: "text-info",
            },
          },
        ],
      },
    ],
  },
  {
    field: "file",
    sections: [
      {
        title: "Detalles",
        fields: [
          {
            key: "fieldName",
            type: "text",
            label: "Nombre del campo",
            placeholder: "Nombre del campo",
            required: true,
          },
        ],
      },
      {
        title: "Opciones",
        showSeparator: true,
        fields: [
          {
            key: "hasDropdownButton",
            type: "switch",
            label: "¿Agregar botón dropdown al campo?",
            required: true,
            defaultValue: false,
          },
          {
            key: "buttonPosition",
            type: "select",
            label: "Posición del botón",
            required: true,
            options: [
              { value: "left", label: "Izquierda" },
              { value: "right", label: "Derecha" },
            ],
            defaultValue: "left",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueSource",
            type: "radio",
            label: "¿De dónde provienen los valores?",
            required: true,
            options: [
              { value: "database", label: "Desde centro de datos" },
              { value: "manual", label: "Locales" },
            ],
            defaultValue: "database",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueDatacenter",
            type: "datacenter",
            required: true,
            label: "Centro de datos",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
          {
            key: "localList",
            type: "local_list",
            label: "Lista local",
            required: true,
            helperText:
              "El primer valor de la lista será el pre-seleccionado por default",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "manual",
          },
          {
            key: "expandSelectedRecord",
            type: "switch",
            label: "Expandir registro seleccionado",
            required: true,
            defaultValue: false,
            customProps: {
              switchFalseText: "No, generar una sola variable (array)",
            },
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
        ],
      },
      {
        title: "Configuración",
        showSeparator: true,
        fields: [
          {
            key: "placeholder",
            type: "text",
            label: "Placeholder del campo",
            placeholder: "Placeholder del campo",
            required: false,
          },
          {
            key: "allowAnyFileType",
            type: "switch",
            label: "¿Permitir cualquier tipo de archivo?",
            required: true,
            defaultValue: false,
          },
          {
            key: "allowedFileTypes",
            type: "file_types",
            label: "Archivos permitidos",
            placeholder: "Escribe una extensión",
            required: false,
            defaultValue: [],
            condition: (formValues) => formValues.allowAnyFileType === false,
          },
          {
            key: "allowMultipleFiles",
            type: "switch",
            label: "Subir múltiples archivos",
            required: true,
            defaultValue: false,
            customProps: {
              switchFalseText: "No permitir",
            },
          },
          {
            key: "maxFileSize",
            type: "number",
            label: "Tamaño máximo permitido (megabytes)",
            placeholder: "500",
            required: false,
            defaultValue: 500,
            customProps: {
              labelSuffix: "Total",
            },
          },
        ],
      },
    ],
  },
  {
    field: "select",
    sections: [
      {
        title: "Detalles",
        fields: [
          {
            key: "fieldName",
            type: "text",
            label: "Nombre del campo",
            placeholder: "Nombre del campo",
            required: true,
          },
        ],
      },
      {
        title: "Opciones",
        showSeparator: true,
        fields: [
          {
            key: "hasDropdownButton",
            type: "switch",
            label: "¿Agregar botón dropdown al campo?",
            required: true,
            defaultValue: false,
          },
          {
            key: "buttonPosition",
            type: "select",
            label: "Posición del botón",
            required: true,
            options: [
              { value: "left", label: "Izquierda" },
              { value: "right", label: "Derecha" },
            ],
            defaultValue: "left",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueSource",
            type: "radio",
            label: "¿De dónde provienen los valores?",
            required: true,
            options: [
              { value: "database", label: "Desde centro de datos" },
              { value: "manual", label: "Locales" },
            ],
            defaultValue: "database",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueDatacenter",
            type: "datacenter",
            required: true,
            label: "Centro de datos",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
          {
            key: "localList",
            type: "local_list",
            label: "Lista local",
            required: true,
            helperText:
              "El primer valor de la lista será el pre-seleccionado por default",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "manual",
          },
          {
            key: "expandSelectedRecord",
            type: "switch",
            label: "Expandir registro seleccionado",
            required: true,
            defaultValue: false,
            customProps: {
              switchFalseText: "No, generar una sola variable (array)",
            },
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
        ],
      },
      {
        title: "Configuración",
        showSeparator: true,
        fields: [
          {
            key: "placeholder",
            type: "text",
            label: "Placeholder del campo",
            placeholder: "Escribe un placeholder para el campo",
            required: false,
          },
          {
            key: "options_root_source",
            type: "radio",
            label: "¿De dónde provienen los valores?",
            required: true,
            options: [
              { value: "database", label: "Desde centro de datos" },
              { value: "manual", label: "Locales" },
            ],
            defaultValue: "database",
          },
          {
            key: "options_root_datacenter",
            type: "datacenter",
            label: "Lista de selección",
            required: true,
            placeholder: "Selecciona una opción",
            condition: (formValues) =>
              formValues.options_root_source === "database",
          },
          {
            key: "options_root_list",
            type: "local_list",
            label: "Lista local",
            required: true,
            helperText:
              "El primer valor de la lista será el pre-seleccionado por default",
            condition: (formValues) =>
              formValues.options_root_source === "manual",
          },
          {
            key: "multipleSelection",
            type: "switch",
            label: "Permitir selección múltiple",
            required: false,
            defaultValue: false,
          },
          {
            key: "expandSelectedRecordConfig",
            type: "switch",
            label: "Expandir registro seleccionado",
            required: true,
            defaultValue: false,
            customProps: {
              switchTrueText:
                "Si, crear variable por columna del registro elegido.",
            },
          },
          {
            key: "defaultValue",
            type: "select",
            label: "Valor por defecto",
            placeholder: "Selecciona un valor por defecto",
            required: false,
            options: [],
            defaultValue: "",
            customProps: {
              labelSuffix: "Opcional",
              source: "options_root",
              searchable: true,
            },
          },
        ],
      },
    ],
  },
  {
    field: "local_list",
    sections: [
      {
        title: "Detalles",
        fields: [
          {
            key: "fieldName",
            type: "text",
            label: "Nombre del campo",
            placeholder: "Nombre del campo",
            required: true,
          },
        ],
      },
      {
        title: "Opciones",
        showSeparator: true,
        fields: [
          {
            key: "hasDropdownButton",
            type: "switch",
            label: "¿Agregar botón dropdown al campo?",
            required: true,
            defaultValue: false,
          },
          {
            key: "buttonPosition",
            type: "select",
            label: "Posición del botón",
            required: true,
            options: [
              { value: "left", label: "Izquierda" },
              { value: "right", label: "Derecha" },
            ],
            defaultValue: "left",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueSource",
            type: "radio",
            label: "¿De dónde provienen los valores?",
            required: true,
            options: [
              { value: "database", label: "Desde centro de datos" },
              { value: "manual", label: "Locales" },
            ],
            defaultValue: "database",
            condition: (formValues) => formValues.hasDropdownButton === true,
          },
          {
            key: "valueDatacenter",
            type: "datacenter",
            required: true,
            label: "Centro de datos",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
          {
            key: "localList",
            type: "local_list",
            label: "Lista local",
            required: true,
            helperText:
              "El primer valor de la lista será el pre-seleccionado por default",
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "manual",
          },
          {
            key: "expandSelectedRecord",
            type: "switch",
            label: "Expandir registro seleccionado",
            required: true,
            defaultValue: false,
            customProps: {
              switchFalseText: "No, generar una sola variable (array)",
            },
            condition: (formValues) =>
              formValues.hasDropdownButton === true &&
              formValues.valueSource === "database",
          },
        ],
      },
      {
        title: "Configuración",
        showSeparator: true,
        fields: [
          {
            key: "placeholder",
            type: "text",
            label: "Placeholder del campo",
            placeholder: "Escribe un placeholder para el campo",
            required: false,
          },
          {
            key: "options_root_source",
            type: "radio",
            label: "¿De dónde provienen los valores?",
            required: true,
            options: [
              { value: "variable", label: "Desde variable" },
              { value: "manual", label: "Locales" },
            ],
            defaultValue: "variable",
          },
          {
            key: "options_root_datacenter",
            type: "datacenter",
            required: true,
            label: "Centro de datos",
            condition: (formValues) =>
              formValues.options_root_source === "variable",
          },
          {
            key: "options_root_list",
            type: "local_list",
            label: "Lista local",
            required: true,
            helperText:
              "El primer valor de la lista será el pre-seleccionado por default",
            condition: (formValues) =>
              formValues.options_root_source === "manual",
          },
          {
            key: "multipleSelection",
            type: "switch",
            label: "Permitir selección múltiple",
            required: false,
            defaultValue: false,
          },
          {
            key: "defaultValue",
            type: "select",
            label: "Valor por defecto",
            placeholder: "Selecciona un valor por defecto",
            required: false,
            options: [{ value: "list_item", label: "Item de la lista" }],
            defaultValue: "",
            customProps: {
              labelSuffix: "Opcional",
            },
          },
        ],
      },
    ],
  },
];
